import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core"
import PropTypes from "prop-types"
import { useContext, useEffect, useMemo } from "react"
import { useDebounce } from "use-lodash-debounce"
import CounterHeader from "~/components/Common/CounterHeader"
import { courseSettingsList, ssoSettingsList } from "~/constants/courses"
import AppContext from "~/contexts/App"
import Course from "~/contexts/Course"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const CourseSettings = ({ tags, categories }) => {
  const classes = useStyles()

  const {
    debouncedDetails,
    course,
    setPricingContext,
    pricingContext,
    setStripePrice,
    stripePrice,
    lengthError,
    tryPublish,
    courseDetailsValues,
    setCourseDetailsValues,
    setInitialDetailsContext,
    setDebouncedDetails,
    unsavedState,
  } = useContext(Course)
  const { setPageSubTitle, authUser } = useContext(AppContext)
  const debouncedLocalValues = useDebounce(courseDetailsValues, 200)

  const mailgunSubjectError =
    (debouncedDetails?.mailgun_subject?.length > 256 && tryPublish) ||
    (lengthError && debouncedDetails?.mailgun_subject?.length > 256)

  const vaasLogoRedirectError =
    (debouncedDetails?.vaas_logo_redirect?.length > 255 && tryPublish) ||
    (lengthError && debouncedDetails?.vaas_logo_redirect?.length > 255)

  //checking if Mailgun Data is in JSON format
  const mailgunDataError = useMemo(() => {
    if (
      debouncedDetails?.mailgun_data === null ||
      debouncedDetails?.mailgun_data === ""
    ) {
      return false
    } else {
      try {
        JSON.parse(debouncedDetails?.mailgun_data)
      } catch (error) {
        return true
      }
      return false
    }
  }, [debouncedDetails?.mailgun_data])

  //set course subtitle to empty string
  useEffect(() => {
    setPageSubTitle("")
  }, [])

  // sets pricing to specific radio button / if paid, returns stripePrice in dollars
  // useEffect(() => {
  //   if (course?.pricing !== "free" && course?.pricing !== "membership") {
  //     setPricingContext("paid")
  //     const priceFormatted = (course?.pricing / 100).toFixed(2)
  //     setStripePrice(priceFormatted)
  //   } else {
  //     setPricingContext(course?.pricing)
  //     setStripePrice("")
  //   }
  // }, [])

  // set course details to component state
  useEffect(() => {
    if (course?.id) {
      const courseData = {
        name: course.name,
        description: course.description || "",
        headline: course.headline || "",
        is_public: Boolean(course.is_public) ?? false,
        discover_page: Boolean(course.discover_page) ?? false,
        mailgun_template_id: course.mailgun_template_id || "",
        mailgun_subject: course.mailgun_subject || "",
        mailgun_data: course.mailgun_data || "",
        promoted: Boolean(course.promoted) ?? false,
        show_latest_version: Boolean(course.show_latest_version) ?? false,
        disable_learn: Boolean(course.disable_learn) ?? false,
        course_type: course.course_type || "course",
        facebook_sso_required: Boolean(course.facebook_sso_required) ?? false,
        meta_quest_sso_required:
          Boolean(course.meta_quest_sso_required) ?? false,
        google_sso_required: Boolean(course.google_sso_required) ?? false,
        disable_final_assessment:
          Boolean(course.disable_final_assessment) ?? false,
        hide_assessment_score: Boolean(course.hide_assessment_score) ?? false,
        vaas_logo_redirect: course.vaas_logo_redirect || "",
        vaas_show_feedback: Boolean(course.vaas_show_feedback) ?? false,
      }
      setCourseDetailsValues({ ...courseData })
      setInitialDetailsContext({ ...courseData })
    }
  }, [course])

  //set details context state from this component state using debounce
  useEffect(() => {
    setDebouncedDetails(courseDetailsValues)
  }, [debouncedLocalValues])
  const renderMailgunFields = () => {
    return (
      <Box marginTop="16px">
        <Typography variant="h3" color="primary" className={classes.inputLabel}>
          Mailgun
        </Typography>
        <div className={classes.twoFieldContainer}>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Box marginTop="8px">
              <Typography color="secondary" className={classes.inputLabel}>
                Mailgun Template ID
              </Typography>
              <TextField
                value={courseDetailsValues?.mailgun_template_id || ""}
                onChange={(e) => {
                  setCourseDetailsValues((oldState) => ({
                    ...oldState,
                    mailgun_template_id: e.target.value,
                  }))
                }}
                style={{ marginBottom: "10px" }}
                InputProps={{
                  classes: {
                    root: classes.inputBackgroundColor,
                  },
                }}
                variant="filled"
                size="small"
                fullWidth
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection={"column"} width={"100%"}>
            <Box marginTop="8px">
              <CounterHeader
                title="Mailgun Subject"
                value={debouncedDetails?.mailgun_subject}
                max={256}
              />
              <TextField
                value={courseDetailsValues?.mailgun_subject || ""}
                onChange={(e) => {
                  setCourseDetailsValues((oldState) => ({
                    ...oldState,
                    mailgun_subject: e.target.value,
                  }))
                }}
                style={{ marginBottom: "10px" }}
                InputProps={{
                  classes: {
                    root: classes.inputBackgroundColor,
                  },
                }}
                variant="filled"
                size="small"
                fullWidth
              />
              {mailgunSubjectError ? (
                <Typography className={classes.requiredText}>
                  Mailgun Subject must be less than 256 characters
                </Typography>
              ) : null}
            </Box>
          </Box>
        </div>
        <Box marginTop="8px">
          <Typography color="secondary" className={classes.inputLabel}>
            Mailgun Data (JSON)
          </Typography>
          <TextField
            value={courseDetailsValues?.mailgun_data || ""}
            multiline
            maxRows={10}
            onChange={(e) => {
              setCourseDetailsValues((oldState) => ({
                ...oldState,
                mailgun_data: e.target.value,
              }))
            }}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: classes.inputDescription,
              },
            }}
            size="small"
            fullWidth
          />
          {mailgunDataError ? (
            <Typography className={classes.requiredText}>
              Mailgun Data must be JSON
            </Typography>
          ) : null}
        </Box>
      </Box>
    )
  }

  const isSettingDisabled = (type) => {
    switch (type) {
      case "disable_final_assessment":
        return Boolean(course?.published || course?.assessment?.id)
      case "hide_assessment_score":
        return Boolean(!course?.assessment?.id)
      default:
        return false
    }
  }
  return (
    <Box className={classes.container}>
      <Box display="flex" justifyContent="flex-start" flexWrap="wrap">
        <Box className={classes.columnContainer}>
          <Typography variant="h3" color="primary" className={classes.title}>
            Course Settings
          </Typography>
          {courseSettingsList.map((setting) => {
            return (
              <Box display="flex" key={setting?.dbValue} alignItems="center">
                <Typography color="secondary" className={classes.inputLabel}>
                  {setting?.title}
                </Typography>
                <Switch
                  disabled={isSettingDisabled(setting?.dbValue)}
                  onChange={(e) => {
                    setCourseDetailsValues((oldState) => ({
                      ...oldState,
                      [setting?.dbValue]: e.target.checked,
                    }))
                  }}
                  checked={Boolean(courseDetailsValues[setting?.dbValue])}
                />
              </Box>
            )
          })}
          {course?.published === 0 && (
            <Box display="flex" alignItems="center">
              <Typography color="secondary" className={classes.inputLabel}>
                Disable Learn
              </Typography>
              <Switch
                disabled={isSettingDisabled("disable_learn")}
                onChange={(e) => {
                  setCourseDetailsValues((oldState) => ({
                    ...oldState,
                    disable_learn: e.target.checked,
                  }))
                }}
                checked={Boolean(courseDetailsValues.disable_learn)}
              />
            </Box>
          )}
        </Box>
        <Box className={classes.columnContainer}>
          <Typography variant="h3" color="primary" className={classes.title}>
            SSO Settings
          </Typography>
          {ssoSettingsList.map((setting) => {
            return (
              <Box display="flex" key={setting?.dbValue} alignItems="center">
                <Typography color="secondary" className={classes.inputLabel}>
                  {setting?.title}
                </Typography>
                <Switch
                  onChange={(e) => {
                    setCourseDetailsValues((oldState) => ({
                      ...oldState,
                      [setting?.dbValue]: e.target.checked,
                    }))
                  }}
                  checked={Boolean(courseDetailsValues[setting?.dbValue])}
                />
              </Box>
            )
          })}
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" marginTop="24px">
        {/* <Box className={classes.columnContainer}>
          <Typography
            variant="h3"
            color="primary"
            className={classes.inputLabel}
          >
            Pricing
          </Typography>
          <RadioGroup
            value={pricingContext}
            onChange={(e) => setPricingContext(e.target.value)}
          >
            <Box>
              <FormControlLabel label="Free" value="free" control={<Radio />} />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControlLabel value="paid" label="Paid" control={<Radio />} />
              {pricingContext === "paid" && (
                <TextField
                  InputProps={{
                    classes: {
                      root: classes.inputBackgroundColor,
                    },
                    inputComponent: NumberFormatCustom,
                  }}
                  variant="filled"
                  size="small"
                  placeholder="Add a stripe price"
                  onChange={(e) => setStripePrice(e.target.value)}
                  value={stripePrice}
                />
              )}
            </Box>
            {pricingContext === "paid" && stripePrice === "" && tryPublish ? (
              <Typography className={classes.requiredText}>
                Please enter a valid price
              </Typography>
            ) : null}
          </RadioGroup>
        </Box> */}
        <Box className={classes.columnContainer}>
          <Typography
            variant="h3"
            color="primary"
            className={classes.inputLabel}
          >
            Course Type
          </Typography>
          <RadioGroup
            value={courseDetailsValues?.course_type}
            onChange={(e) => {
              setCourseDetailsValues((oldState) => ({
                ...oldState,
                course_type: e.target.value,
              }))
              return
            }}
          >
            <Box key={courseDetailsValues?.course_type}>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  label="Course"
                  value="course"
                  control={
                    <Radio
                      value="course"
                      checked={courseDetailsValues?.course_type === "course"}
                    />
                  }
                />
              </Box>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  label="Shortcode"
                  value="shortcode"
                  control={
                    <Radio
                      value="shortcode"
                      checked={courseDetailsValues?.course_type === "shortcode"}
                    />
                  }
                />
              </Box>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  label="VAAS"
                  value="vaas"
                  control={
                    <Radio
                      value="vaas"
                      checked={courseDetailsValues?.course_type === "vaas"}
                    />
                  }
                />
              </Box>
            </Box>
          </RadioGroup>
        </Box>
      </Box>
      {/* <PermissionsWrapper permissionGroup="super">
        <>{renderMailgunFields()}</>
      </PermissionsWrapper> */}
      {courseDetailsValues?.course_type === "vaas" && (
        <>
          <Box display="flex" alignItems="center" marginTop="8px">
            <Typography color="secondary" className={classes.inputLabel}>
              Show Vaas Feedback
            </Typography>
            <Switch
              onChange={(e) => {
                setCourseDetailsValues((oldState) => ({
                  ...oldState,
                  vaas_show_feedback: e.target.checked,
                }))
              }}
              checked={Boolean(courseDetailsValues.vaas_show_feedback)}
            />
          </Box>
          <Box marginTop="8px">
            <CounterHeader
              title="Vaas Logo Redirect"
              value={debouncedDetails?.vaas_logo_redirect}
              max={2000}
            />

            <TextField
              value={courseDetailsValues?.vaas_logo_redirect || ""}
              onChange={(e) => {
                setCourseDetailsValues((oldState) => ({
                  ...oldState,
                  vaas_logo_redirect: e.target.value,
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
          {vaasLogoRedirectError ? (
            <Typography className={classes.requiredText}>
              Vaas logo redirect must be less than 2000 characters
            </Typography>
          ) : null}
          <Typography color="secondary" className={classes.inputLabel}>
            Vaas Video Locations
          </Typography>
          <div className="space-y-2">
            {course?.vaas_locations[0]?.urls?.map((location, index) => (
              <Typography key={index} className="mt-2">
                {location}
              </Typography>
            ))}
          </div>
        </>
      )}
    </Box>
  )
}

CourseSettings.propTypes = {
  courseId: PropTypes.string,
  tags: PropTypes.array,
  categories: PropTypes.array,
  checked: PropTypes.bool,
}

export default CourseSettings
